import { useState, useEffect }  from 'react';
import { useNavigate }           from 'react-router-dom';
import { useSessionStore }       from '../store/sessionStore.js';
import { useAuthStore }          from '../store/authStore.js';
import { usePrinterStore }       from '../store/printerStore.js';
import { getPaymentsBySession, getBusinessInfo, sendReceiptByEmail } from '../api/pos.api.js';
import { thermalPrinter }        from '../native/thermalPrinter.js';
import { IcoCash, IcoCard, IcoTransfer, IcoMixed, IcoReceipt, IcoNote, IcoPrint, IcoCheck, IcoClose, IcoEmail, IcoReturn, IcoDownload } from '../components/common/Icons.jsx';
import styles from './SalesHistoryScreen.module.css';

const fmt     = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

const METHOD_LABEL = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', mixed: 'Mixto' };
const METHOD_ICON  = { cash: IcoCash, card: IcoCard, transfer: IcoTransfer, mixed: IcoMixed };

export default function SalesHistoryScreen() {
  const navigate      = useNavigate();
  const session       = useSessionStore((s) => s.session);
  const user          = useAuthStore((s) => s.user);
  const printerDevId  = usePrinterStore((s) => s.deviceId);
  const printerTrans  = usePrinterStore((s) => s.transport);

  const [payments,   setPayments]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [business,   setBusiness]   = useState(null);
  const [expanded,   setExpanded]   = useState(null);
  const [printing,   setPrinting]   = useState(null);
  const [printErr,   setPrintErr]   = useState(null);
  const [filter,     setFilter]     = useState('');
  const [emailFor,   setEmailFor]   = useState(null);  // payment._id showing email input
  const [emailVal,   setEmailVal]   = useState('');
  const [sending,    setSending]    = useState(null);
  const [sendOk,     setSendOk]     = useState(null);

  useEffect(() => {
    if (!session?._id) return;
    getPaymentsBySession(session._id)
      .then((res) => {
        const list = res?.data ?? res ?? [];
        setPayments(Array.isArray(list) ? list : []);
      })
      .catch(() => setError('No se pudo cargar el historial'))
      .finally(() => setLoading(false));
    getBusinessInfo().then(setBusiness).catch(() => {});
  }, [session?._id]);

  const totals = payments.reduce(
    (acc, p) => {
      if (p.status === 'refunded') return acc;
      acc.count += 1;
      const effective = p.status === 'partially_refunded'
        ? (p.total ?? 0) - (p.refund_amount ?? 0)
        : (p.total ?? 0);
      acc.total += effective;
      return acc;
    },
    { count: 0, total: 0 },
  );

  const filtered = filter.trim()
    ? [...payments].reverse().filter((p) => {
        const q = filter.trim().toUpperCase();
        return (
          (p.client_uuid ?? p._id).toUpperCase().includes(q) ||
          (p.customer_name ?? '').toUpperCase().includes(q) ||
          (p.items ?? []).some((i) => i.name.toUpperCase().includes(q))
        );
      })
    : [...payments].reverse();

  const handleReprint = async (p) => {
    setPrintErr(null);
    setPrinting(p._id);
    try {
      if (printerDevId && !thermalPrinter.isConnected()) {
        await thermalPrinter.autoReconnect(printerDevId, printerTrans).catch(() => {});
      }
      await thermalPrinter.printReceipt({
        business,
        date:     fmtTime(p.createdAt || p.created_at || new Date().toISOString()),
        cashier:  user?.name ?? user?.email ?? null,
        customer: p.customer_name ?? null,
        folio:    (p.client_uuid ?? p._id).slice(-8).toUpperCase(),
        items:    (p.items ?? []).map((i) => ({
          name:           i.name,
          quantity:       i.quantity,
          unit_price:     i.unit_price,
          original_price: i.original_price ?? undefined,
        })),
        subtotal:  p.total ?? 0,
        discount:  p.discount ?? 0,
        total:     p.total ?? 0,
        method:    p.method ?? 'cash',
        changeDue: p.change_given ?? 0,
        note:      p.note ?? null,
      });
    } catch (err) {
      setPrintErr('Error al imprimir: ' + (err.message ?? 'Intenta de nuevo'));
    } finally {
      setPrinting(null);
    }
  };

  const toggleExpand = (id) => { setExpanded((prev) => (prev === id ? null : id)); setEmailFor(null); };

  const handleSendEmail = async (p) => {
    if (!emailVal.trim()) return;
    setSending(p._id);
    try {
      await sendReceiptByEmail(p._id, { email: emailVal.trim(), items: p.items });
      setSendOk(p._id);
      setEmailFor(null);
      setEmailVal('');
      setTimeout(() => setSendOk(null), 3000);
    } catch {
      // silently keep the input open so user can retry
    } finally {
      setSending(null);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Folio', 'Hora', 'Método', 'Cliente', 'Artículos', 'Total', 'Estado'],
      ...payments.map((p) => [
        (p.client_uuid ?? p._id).slice(-8).toUpperCase(),
        p.createdAt ? new Date(p.createdAt).toLocaleString('es-MX') : '',
        METHOD_LABEL[p.method] ?? p.method,
        p.customer_name ?? '',
        (p.items ?? []).map((i) => `${i.quantity}x ${i.name}`).join(' | '),
        p.total,
        p.status,
      ]),
    ];
    const csv  = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `ventas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        {/* Header */}
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Ventas de la sesión</h1>
        </div>

        {/* Resumen */}
        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Ventas</span>
            <strong className={styles.summaryValue}>{totals.count}</strong>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Total cobrado</span>
            <strong className={styles.summaryValue}>{fmt(totals.total)}</strong>
          </div>
          {payments.length > 0 && (
            <button
              className={styles.csvBtn}
              onClick={exportCSV}
              title="Exportar CSV"
            >
              <IcoDownload size={13} /> CSV
            </button>
          )}
        </div>

        {/* Filtro */}
        {!loading && payments.length > 0 && (
          <input
            className={`input ${styles.filterInput}`}
            type="search"
            placeholder="Buscar por folio, cliente o producto…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        )}

        {printErr && <p className={styles.errorMsg}>{printErr}</p>}

        {/* Lista */}
        {loading ? (
          <div className={styles.centered}><span className="spinner" /></div>
        ) : error ? (
          <p className={styles.errorMsg}>{error}</p>
        ) : payments.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyIcon}><IcoReceipt size={40} /></p>
            <p className={styles.emptyText}>Sin ventas en esta sesión</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>Sin resultados para "{filter}"</p>
          </div>
        ) : (
          <div className={styles.list}>
            {filtered.map((p) => {
              const isOpen = expanded === p._id;
              return (
                <div key={p._id} className={`${styles.rowWrap} ${p.status === 'refunded' ? styles.rowRefunded : ''}`}>
                  <div className={styles.row} onClick={() => toggleExpand(p._id)}>
                    <div className={styles.rowLeft}>
                      <span className={styles.rowIcon}>{(() => { const M = METHOD_ICON[p.method] ?? IcoCash; return <M size={16} />; })()}</span>
                      <div className={styles.rowInfo}>
                        <span className={styles.rowFolio}>#{(p.client_uuid ?? p._id).slice(-8).toUpperCase()}</span>
                        <span className={styles.rowMeta}>
                          {METHOD_LABEL[p.method] ?? p.method}
                          {p.customer_name && <span className={styles.customerTag}> · {p.customer_name}</span>}
                          {p.status === 'refunded'           && <span className={styles.refundBadge}> · Devuelto</span>}
                          {p.status === 'partially_refunded' && <span className={styles.partialBadge}> · Parcial</span>}
                        </span>
                        {p.items?.length > 0 && (
                          <span className={styles.rowItems}>
                            {p.items.slice(0, 2).map((i) => i.name).join(', ')}
                            {p.items.length > 2 ? ` +${p.items.length - 2}` : ''}
                          </span>
                        )}
                        {p.note && (
                          <span className={styles.rowNote}><IcoNote size={11} /> {p.note}</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <span className={styles.rowTotal}>{fmt(p.total)}</span>
                      <span className={styles.rowTime}>{p.createdAt ? fmtTime(p.createdAt) : ''}</span>
                    </div>
                  </div>

                  {/* Acciones expandidas */}
                  {isOpen && (
                    <div className={styles.rowActions}>
                      <button
                        className={`btn btn--ghost ${styles.actionBtn}`}
                        onClick={() => handleReprint(p)}
                        disabled={!!printing}
                      >
                        {printing === p._id ? <span className="spinner" /> : <><IcoPrint size={14} /> Reimprimir</>}
                      </button>
                      {sendOk === p._id ? (
                        <span className={styles.sentOk}><IcoCheck size={13} /> Enviado</span>
                      ) : emailFor === p._id ? (
                        <div className={styles.emailRow}>
                          <input
                            className={styles.emailInput}
                            type="email"
                            placeholder="correo@ejemplo.com"
                            value={emailVal}
                            autoFocus
                            onChange={(e) => setEmailVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmail(p); if (e.key === 'Escape') setEmailFor(null); }}
                          />
                          <button className="btn btn--primary" style={{ fontSize: 12, minHeight: 32, padding: '0 10px', flexShrink: 0 }} onClick={() => handleSendEmail(p)} disabled={sending === p._id}>
                            {sending === p._id ? <span className="spinner" /> : 'Enviar'}
                          </button>
                          <button className="btn btn--ghost" style={{ fontSize: 12, minHeight: 32, padding: '0 8px', flexShrink: 0 }} onClick={() => setEmailFor(null)}><IcoClose size={13} /></button>
                        </div>
                      ) : (
                        <button
                          className={`btn btn--ghost ${styles.actionBtn}`}
                          onClick={() => { setEmailFor(p._id); setEmailVal(p.customer_email ?? ''); }}
                        >
                          <IcoEmail size={14} /> Email
                        </button>
                      )}
                      {p.status !== 'refunded' && (
                        <button
                          className={`btn btn--ghost ${styles.actionBtn}`}
                          onClick={() => navigate('/refund', { state: { payment: p } })}
                        >
                          <IcoReturn size={14} /> Devolver
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
